import { Vector3 } from "three";
import { cylinderDistance, type CylinderSurface } from "./cylinder-surface";
import {
  fitPipeStep,
  fitPipeTransition,
  pipeSamplesFromPoints,
  pipeStepLength,
  pipeTransitionRadius,
  pipeSampleIndex,
  pipeNeighbors,
  type PipeFrontier,
  type PipeStep,
  type PipeTransition,
} from "./pipe-run";

export type PipeEvidence = {
  points: Vector3[];
  spacing: number;
  supportSpacing?: number;
  complete: boolean;
};
export type PipeQuery = (
  center: Vector3,
  radius: number,
  signal: AbortSignal,
  spacing?: number,
) => Promise<PipeEvidence>;
export type PipeTraceSolver = {
  seed?(
    evidence: PipeEvidence,
    clicked: Vector3,
    cancelled: () => boolean,
  ): Promise<CylinderSurface | undefined>;
  step(
    evidence: PipeEvidence,
    frontier: PipeFrontier,
    cancelled: () => boolean,
  ): Promise<PipeStep | undefined>;
  transition(
    evidence: PipeEvidence,
    frontier: PipeFrontier,
    cancelled: () => boolean,
  ): Promise<PipeTransition>;
};
export type PipeTraceStop =
  | "unsupported"
  | "incomplete"
  | "branch"
  | "cancelled"
  | "revisited"
  | "resource-limit";
export type PipeTraceUpdate = {
  direction: -1 | 1;
  kind: "step" | "fitting" | "gap";
  from: Vector3;
  step: PipeStep;
  inferredLength: number;
};
export type PipeTraceEnd = {
  direction: -1 | 1;
  frontier: PipeFrontier;
  reason: PipeTraceStop;
  candidates: PipeStep[];
  acceptedSteps?: number;
  length?: number;
  detail?: "fit-rejected" | "ambiguous-transition";
};
export type PipeTraceResult = {
  ends: PipeTraceEnd[];
  length: number;
  inferredLength: number;
  steps: number;
};

const LIMITS = {
  windowRadius: 2.5,
  spillRadius: 2,
  spillSamples: 24,
  spillSpacingRatio: 2.5,
  spillRadiusFraction: 0.25,
  gapDirectionDivisor: 18,
  maximumGapRadii: 4,
  visitedCapacity: 65_536,
  visitCellRatio: 0.25,
};

/** @returns A local solver using position-derived normals and the real geometric backend. */
export function createPipeTraceSolver(): PipeTraceSolver {
  const normals = new WeakMap<Vector3, Vector3 | undefined>();
  return {
    step: (evidence, frontier, cancelled) =>
      fitPipeStep(
        pipeSamplesFromPoints(
          evidence.points,
          evidence.spacing,
          normals,
          evidence.supportSpacing,
        ),
        frontier,
        cancelled,
      ),
    transition: (evidence, frontier, cancelled) =>
      fitPipeTransition(
        pipeSamplesFromPoints(
          evidence.points,
          evidence.spacing,
          normals,
          evidence.supportSpacing,
        ),
        frontier,
        cancelled,
      ),
  };
}

/**
 * @param seed Accepted straight seed at the intended starting position.
 * @param query Source-scoped bounded neighborhood query; missing data must be explicit.
 * @param options Cancellation, solver ownership and accepted-update publication.
 * @returns Both frontier outcomes; unsupported coverage is never reported as a physical pipe end.
 */
export async function tracePipeRun(
  seed: CylinderSurface,
  query: PipeQuery,
  options: {
    signal: AbortSignal;
    solver?: PipeTraceSolver;
    accept?(update: PipeTraceUpdate): boolean;
    publish?(update: PipeTraceUpdate): void;
  },
): Promise<PipeTraceResult> {
  const { signal, publish, solver = createPipeTraceSolver() } = options;
  const result: PipeTraceResult = {
    ends: [],
    length: 0,
    inferredLength: 0,
    steps: 0,
  };
  const pending: Array<{
    direction: -1 | 1;
    frontier: PipeFrontier;
    acceptedSteps: number;
    length: number;
  }> = [
    {
      direction: -1,
      acceptedSteps: 0,
      length: 0,
      frontier: {
        center: seed.center.clone(),
        axis: seed.axis.clone().negate(),
        radius: seed.radius,
      },
    },
    {
      direction: 1,
      acceptedSteps: 0,
      length: 0,
      frontier: {
        center: seed.center.clone(),
        axis: seed.axis.clone(),
        radius: seed.radius,
      },
    },
  ];
  const visited = new Set<string>();
  const cellSize = seed.radius * LIMITS.visitCellRatio;
  if (
    !(cellSize > 0) ||
    !Number.isFinite(cellSize) ||
    ![...seed.center.toArray(), ...seed.axis.toArray()].every(
      Number.isFinite,
    ) ||
    seed.axis.lengthSq() === 0
  ) {
    throw new Error("Invalid pipe seed");
  }
  function key(point: Vector3): string {
    return point
      .clone()
      .sub(seed.center)
      .divideScalar(cellSize)
      .toArray()
      .map(Math.round)
      .join(",");
  }
  visited.add(key(seed.center));
  function cancelled(): boolean {
    return signal.aborted;
  }
  while (pending.length) {
    const current = pending.shift();
    if (!current) break;
    const { direction, frontier, acceptedSteps, length: travelled } = current;
    function stop(
      reason: PipeTraceStop,
      candidates: PipeStep[] = [],
      detail?: PipeTraceEnd["detail"],
    ): void {
      result.ends.push({
        direction,
        frontier,
        reason,
        candidates,
        detail,
        acceptedSteps,
        length: travelled,
      });
    }
    if (cancelled()) {
      stop("cancelled");
      continue;
    }
    if (visited.size >= LIMITS.visitedCapacity) {
      stop("resource-limit");
      continue;
    }
    const advance = pipeStepLength(frontier.radius);
    const predicted = frontier.center
      .clone()
      .addScaledVector(frontier.axis, advance);
    let evidence: PipeEvidence;
    try {
      evidence = await query(
        predicted,
        frontier.radius * LIMITS.windowRadius,
        signal,
        frontier.radius / 12,
      );
    } catch {
      stop(cancelled() ? "cancelled" : "incomplete");
      continue;
    }
    if (cancelled()) {
      stop("cancelled");
      continue;
    }
    if (!evidence.complete) {
      stop("incomplete");
      continue;
    }
    let accepted: PipeStep | undefined;
    try {
      accepted = await solver.step(evidence, frontier, cancelled);
    } catch {
      stop(cancelled() ? "cancelled" : "incomplete");
      continue;
    }
    if (cancelled()) {
      stop("cancelled");
      continue;
    }
    let kind: PipeTraceUpdate["kind"] = "step";
    let inferredLength = 0;
    let failure: PipeTraceStop = "unsupported";
    let spill = 0;
    if (accepted) {
      const outliers: Vector3[] = [];
      for (const point of evidence.points) {
        const offset = point.clone().sub(accepted.frontier.center);
        if (
          Math.abs(offset.dot(accepted.frontier.axis)) <= advance &&
          offset.length() < frontier.radius * LIMITS.spillRadius &&
          Math.abs(cylinderDistance(accepted.cylinder, point)) >
            accepted.tolerance * 2
        ) {
          outliers.push(point);
        }
      }
      if (outliers.length >= LIMITS.spillSamples) {
        const support = pipeSampleIndex(accepted.samples);
        const reach = Math.min(
          frontier.radius * LIMITS.spillRadiusFraction,
          Math.max(
            evidence.spacing,
            evidence.supportSpacing ?? evidence.spacing,
          ) * LIMITS.spillSpacingRatio,
        );
        for (const point of outliers) {
          if (pipeNeighbors(support, point, reach, 1).length) spill++;
        }
      }
    }
    if (!accepted || spill >= LIMITS.spillSamples) {
      let transitionEvidence: PipeEvidence;
      try {
        transitionEvidence = await query(
          frontier.center,
          pipeTransitionRadius(frontier.radius),
          signal,
          frontier.radius / 12,
        );
      } catch {
        stop(cancelled() ? "cancelled" : "incomplete");
        continue;
      }
      if (cancelled()) {
        stop("cancelled");
        continue;
      }
      if (!transitionEvidence.complete) {
        stop("incomplete");
        continue;
      }
      let transition: PipeTransition;
      try {
        transition = await solver.transition(
          transitionEvidence,
          frontier,
          cancelled,
        );
      } catch {
        stop(cancelled() ? "cancelled" : "incomplete");
        continue;
      }
      if (cancelled()) {
        stop("cancelled");
        continue;
      }
      if (transition.kind === "junction") {
        stop("branch", transition.outlets);
        continue;
      }
      if (!accepted && transition.kind === "outlet") {
        const [outlet] = transition.outlets;
        accepted = { ...outlet, samples: transition.samples };
        kind = "fitting";
      } else if (
        spill >= LIMITS.spillSamples &&
        transition.kind === "unresolved"
      ) {
        stop("unsupported", [], "ambiguous-transition");
        continue;
      }
      if (!accepted) {
        for (
          let gap = advance;
          gap <= frontier.radius * LIMITS.maximumGapRadii;
          gap += advance
        ) {
          if (cancelled()) break;
          const candidate = {
            ...frontier,
            center: frontier.center.clone().addScaledVector(frontier.axis, gap),
          };
          let ahead: PipeEvidence;
          try {
            ahead = await query(
              candidate.center.clone().addScaledVector(candidate.axis, advance),
              frontier.radius * LIMITS.windowRadius,
              signal,
              frontier.radius / 12,
            );
          } catch {
            failure = "incomplete";
            break;
          }
          if (!ahead.complete) {
            failure = "incomplete";
            break;
          }
          let step: PipeStep | undefined;
          try {
            step = await solver.step(ahead, candidate, cancelled);
          } catch {
            failure = "incomplete";
            break;
          }
          if (
            !step ||
            step.frontier.axis.dot(frontier.axis) <
              Math.cos(Math.PI / LIMITS.gapDirectionDivisor) ||
            Math.abs(step.frontier.radius / frontier.radius - 1) > 0.1
          ) {
            continue;
          }
          const lateral = step.frontier.center
            .clone()
            .sub(frontier.center)
            .cross(frontier.axis)
            .length();
          if (lateral > step.tolerance * 2) continue;
          accepted = step;
          kind = "gap";
          inferredLength = frontier.center.distanceTo(step.frontier.center);
          break;
        }
      }
    }
    if (cancelled()) {
      stop("cancelled");
      continue;
    }
    if (!accepted) {
      stop(failure, [], failure === "unsupported" ? "fit-rejected" : undefined);
      continue;
    }
    const nextKey = key(accepted.frontier.center);
    if (visited.has(nextKey)) {
      stop("revisited");
      continue;
    }
    const update: PipeTraceUpdate = {
      direction,
      kind,
      from: frontier.center.clone(),
      step: accepted,
      inferredLength,
    };
    if (options.accept?.(update) === false) {
      stop("resource-limit");
      continue;
    }
    visited.add(nextKey);
    const length = frontier.center.distanceTo(accepted.frontier.center);
    result.length += length;
    result.inferredLength += inferredLength;
    result.steps++;
    publish?.(update);
    pending.push({
      direction,
      frontier: accepted.frontier,
      acceptedSteps: acceptedSteps + 1,
      length: travelled + length,
    });
  }
  return result;
}
